import {
  useCanvasFileRecords,
  useHydrateFileRecordEntity,
} from "@/api/fileRecords";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import {
  buildCanvasNodeKind,
  getActionIcon,
  getActionLabel,
  getObjectActionDefinition,
  resolvedActionsToContextMenuActions,
  useCanvasEntityObjectActions,
} from "@/object-actions";
import type { ObjectActionId } from "@/object-actions/types";
import { useCanvasEntityNavigationHandlers } from "@/pages/canvas/hooks/useCanvasEntityNavigationHandlers";
import { useCanvasFileRecordMenuHandlerDeps } from "@/pages/canvas/hooks/useCanvasFileRecordMenuHandlerDeps";
import {
  useHtmlBlockNode,
  type HtmlBlockNodeState,
} from "@/pages/canvas/hooks/useHtmlBlockNode";
import type { EntityNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { openCanvasEntityTabUrl } from "@/pages/canvas/utils/resolveCanvasEntityOpenTabUrl";
import { resolveFileRecordFromList } from "@/pages/canvas/utils/resolveFileRecordFromList";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import {
  BaseEntityCardFloatingToolbar,
  BaseEntityCardQuickChat,
  CanvasEntityCard,
  FileRecordCardSkeleton,
  getEntityData,
  type ContextMenuAction,
  type Entity,
  type FileRecordSkeletonKind,
} from "@cohesive/ui-components";
import { NodeResizer, useUpdateNodeInternals } from "@xyflow/react";
import { AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  ExternalLink,
  FileText,
  Frame,
  MousePointerClick,
  Play,
} from "lucide-react";
import {
  createElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { LiveProcess } from "../components/LiveProcess";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useHtmlBlockStreamPreview } from "../context/CanvasHtmlStreamPreviewContext";
import {
  useCanvasLocalFilePreview,
  useCanvasLocalFilePreviewActions,
} from "../context/CanvasLocalFilePreviewContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useOpenNodeContextMenu } from "../hooks/useOpenNodeContextMenu";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { useVideoHandoffReady } from "../hooks/useVideoHandoffReady";
import {
  fileRecordIsProcessed,
  mergeFileRecordWithLocalPreview,
  shouldRevokeLocalFilePreview,
  shouldShowFileRecordSkeleton,
} from "../utils/fileRecordDisplay";
import { resolveGenericEntityPaneTarget } from "../utils/genericEntityPane";
import {
  DEFAULT_FILE_NODE_HEIGHT,
  getEntityMediaDisplayBox,
  getFileRecordMediaDisplayBoxFromMetadata,
} from "../utils/imageDimensions";
import { HtmlBlockGripStrip } from "./HtmlBlockGripStrip";
import { HtmlBlockStreamingGhost } from "./HtmlBlockStreamingGhost";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

// Temporary: file entity node toolbars only surface these utilities (in this
// order). Chat + delete render separately; the rest live under "More".
const FILE_ENTITY_TOOLBAR_UTILITY_ORDER: ObjectActionId[] = [
  "share",
  "download",
  "open",
];

// Default card width — used when no width is stored in Loro yet.
const DEFAULT_WIDTH = 320;

// Matches fitImageNodeSize(..., minShortEdge); generic file nodes stay at 100.
// One resize floor for every entity card (ruled in M1 review): today's
// canvas file records — media, documents, HTML, tiles — are all scalable
// previews, so they shrink alike. 128 = 8 lattice cells, still reads as
// content. Per-object milestones re-raise specific types when dense DS
// card bodies land (recorded in the geometry matrix when they do).
const ENTITY_RESIZER_MIN = 128;

// Toolbar entry point for block ↔ document conversion, hidden for now (the
// action caused UX confusion). The conversion machinery itself stays live —
// CanvasEdit's per-node `display`, `canvas.removeNodeDataKey`, the in-place
// marker flip — so flipping this back on restores the buttons as-is.
const DISPLAY_MODE_CONVERSION_TOOLBAR = false;

// Several classes of legacy entities are intentionally not rendered on the
// canvas surface anymore. All wrappers return `null` from the React Flow node
// component, which fully hides the node — selection rings, handles, the lot —
// without touching Loro state. Backend cleanup (deleting the rows, dropping
// the supporting tables) happens in a later pass.
//
//   1. `type: "artifact"` — content migrated into file_records.
//
//   2. `base: "file"` AND (platform === "cohesive" OR type === "canvas_file")
//      — the legacy rag_files-backed canvas uploads. External `base: "file"`
//      entities (Google Drive, Dropbox, Slack file shares, etc.) keep
//      rendering through the normal external-file path.
//
//   3. `base: "image"` | `base: "video"` — legacy media nodes.
//      These still render in chat via the legacyFileEntityDataToFileRecord
//      adapter; they are hidden on canvas only.
//
// The guard runs *before* any hooks in the inner component, so we don't pay
// for stale fetches / effects on hidden nodes.
function isLegacyHiddenEntityProps(props: EntityNodeProps): boolean {
  const e = props.data?.entity as
    | { base?: string; type?: string; platform?: string }
    | undefined;
  if (!e) return false;
  if (e.type === "artifact") return true;
  if (e.base === "image" || e.base === "video") return true;
  if (
    e.base === "file" &&
    (e.platform === "cohesive" || e.type === "canvas_file")
  ) {
    return true;
  }
  return false;
}

export const EntityNode = memo(function EntityNode(props: EntityNodeProps) {
  if (isLegacyHiddenEntityProps(props)) return null;
  // Routing on the block marker (rather than mounting the block hooks
  // unconditionally) keeps image/PDF/doc nodes free of the presign map and
  // the other per-block machinery. `data.presentation` is written only on
  // HTML file records. The marker CAN flip in place (display-mode conversion
  // via the toolbar or CanvasEdit): the component identity changes, so React
  // remounts the subtree cleanly — the focus latch releases (and flushes) on
  // unmount, and the other card renders fresh.
  if (props.data?.presentation != null) {
    return <HtmlBlockEntityNode {...props} />;
  }
  return <EntityNodeImpl {...props} htmlBlock={null} />;
});

/** Mounts the HTML-block hooks, then renders the shared node body. */
function HtmlBlockEntityNode(props: EntityNodeProps) {
  const { canEdit } = useCanvasApi();
  const presentation = props.data?.presentation ?? {};
  const fileRecordId = (props.data?.entity?.data as { id?: string } | undefined)
    ?.id;
  const htmlBlock = useHtmlBlockNode({
    nodeId: props.id,
    presentation,
    fileRecordId,
    loading: props.data?.loading === true,
    selected: !!props.selected,
    dragging: !!props.dragging,
    canEdit,
  });
  return <EntityNodeImpl {...props} htmlBlock={htmlBlock} />;
}

function EntityNodeImpl({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
  htmlBlock,
}: EntityNodeProps & { htmlBlock: HtmlBlockNodeState | null }) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  // Presign rewrite for HTML cards WITHOUT a block presentation (legacy and
  // agent-created records — the backend never sets `presentation`). The card
  // iframe is an opaque-origin sandbox that sends no credentials, so embedded
  // public/{id} asset URLs must be swapped for presigned ones before display;
  // block nodes bring their own rewrite (shared with their edit session).
  // Idle cost is refs only — the bulk-presign request fires solely when an
  // HTML card actually renders content that references files.
  const presign = useHtmlPresignRewrite({
    fileRecordId: (data?.entity?.data as { id?: string } | undefined)?.id,
  });
  // Public share view: pace HTML-card iframe mounts through the shared
  // admission scheduler instead of bursting them all on load (the burst
  // kills the WebKit renderer on iPhones for block-heavy canvases). The
  // editor keeps the eager default — byte-identical behavior.
  const isPublicCanvasView = useIsPublicCanvasView();
  const {
    dispatchProcess,
    getProcessesForNode,
    isProcessing,
    highlightedNodeIds,
  } = useCanvasProcess();
  const { isResizingRef } = useCanvasUi();
  // Ghost nodes carry no `presentation` yet (the backend writes it when the
  // real node lands), so the streaming preview subscription lives here rather
  // than in the block wrapper. It is a no-op store read when nothing is
  // streaming for this node, or when no provider is mounted at all.
  const streamPreview = useHtmlBlockStreamPreview(id);
  const isHighlighted = highlightedNodeIds.has(id);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const appNavigate = useAppNavigate();
  const openFileRecord = useOpenFileRecord();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();

  // O(1) — subscribes to only the selection count scalar, not full node array.
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Clear chat latch on multi-select or deselect — node stays selected during
  // multi-select, so !selected alone would leave showChatInput true and remount
  // chat UI when returning to single-select.
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
    }
  }, [isMultiSelectToolbarOpen, selected]);

  const isFileRecordEntity = !!(
    data?.entity &&
    "base" in data.entity &&
    data.entity.base === "file_record"
  );

  const localPreview = useCanvasLocalFilePreview(id);
  const { revoke: revokeLocalPreview } = useCanvasLocalFilePreviewActions();

  const { entity: hydratedFileRecordEntity } = useHydrateFileRecordEntity(
    isFileRecordEntity ? (data?.entity as any) : null,
  );

  // Hydrated file records carry full backend metadata (content_type,
  // preview_url, etc.) that the toolbar / preview render against. Other
  // entity types pass through the raw Loro entity unchanged.
  const effectiveEntity = useMemo(() => {
    if (isFileRecordEntity && hydratedFileRecordEntity) {
      return hydratedFileRecordEntity;
    }
    return data?.entity;
  }, [isFileRecordEntity, data?.entity, hydratedFileRecordEntity]);

  const layoutEntity = effectiveEntity ?? data?.entity;

  const isVideoLocalPreview =
    !!localPreview && localPreview.contentType.startsWith("video/");

  const serverFileRecordData = isFileRecordEntity
    ? (effectiveEntity?.data as
        | {
            status?: string;
            url?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : undefined;

  const serverVideoUrl =
    isVideoLocalPreview &&
    fileRecordIsProcessed(serverFileRecordData?.status) &&
    serverFileRecordData?.url
      ? serverFileRecordData.url
      : undefined;

  const videoHandoffReady = useVideoHandoffReady(
    serverVideoUrl,
    !!serverVideoUrl,
  );

  const mergeOptions = useMemo(
    () => ({ videoHandoffReady }),
    [videoHandoffReady],
  );

  const fileRecordCardEntity = useMemo(() => {
    const base = layoutEntity;
    if (!isFileRecordEntity || !base || !("data" in base) || !localPreview) {
      return base;
    }
    const mergedData = mergeFileRecordWithLocalPreview(
      base.data as {
        status?: string;
        url?: string;
        content_type?: string;
        preview_urls?: Record<string, string> | null;
      },
      localPreview,
      mergeOptions,
    );
    if (mergedData === base.data) return base;
    return { ...base, data: mergedData } as Entity;
  }, [isFileRecordEntity, layoutEntity, localPreview, mergeOptions]);

  // Revoke blob for images once processed; videos revoke in onVideoDisplayReady
  // after the server stream paints (see useVideoHandoffReady + thumbnail hold).
  useEffect(() => {
    if (!localPreview) return;
    if (shouldRevokeLocalFilePreview(serverFileRecordData, localPreview)) {
      revokeLocalPreview(id);
    }
  }, [id, localPreview, serverFileRecordData, revokeLocalPreview]);

  const handleVideoDisplayReady = useCallback(() => {
    if (!localPreview?.contentType.startsWith("video/")) return;
    if (!fileRecordIsProcessed(serverFileRecordData?.status)) return;
    if (!videoHandoffReady) return;
    revokeLocalPreview(id);
  }, [
    id,
    localPreview,
    serverFileRecordData?.status,
    videoHandoffReady,
    revokeLocalPreview,
  ]);

  // Determine resize behaviour from entity type
  const entity = layoutEntity;
  // HTML file records get free-form resize (no aspect lock).
  const isHtmlFileRecord =
    !!entity &&
    "base" in entity &&
    entity.base === "file_record" &&
    ((entity.data as { content_type?: string })?.content_type ?? "")
      .split(";")[0]
      .trim() === "text/html";
  // Progressive-mount placeholder for HTML cards on the public share view:
  // the published artifact carries a server-rendered 640px JPEG per file
  // (enrichPublicCanvasNodes maps files[id].previews → preview_urls, and
  // nulls out stale in-app values so this can never be an expired presigned
  // URL), shown until the scheduler admits the live iframe. Editor passes
  // nothing.
  const htmlPreviewImageUrl =
    isPublicCanvasView && isHtmlFileRecord
      ? serverFileRecordData?.preview_urls?.["640"]
      : undefined;
  // Generic entities with a cover image render as an embedded thumbnail tile
  // (see CanvasEntityCard); treat them like file tiles for sizing + resize.
  const isGenericTile = !!(
    entity &&
    "base" in entity &&
    entity.base === "generic" &&
    (entity.data as { image_url?: string })?.image_url
  );
  const isSquareResizable = !!(
    entity &&
    "base" in entity &&
    (entity.base === "file" ||
      (entity.base === "file_record" && !isHtmlFileRecord) ||
      isGenericTile)
  );
  const fittedMediaBox = entity
    ? (getEntityMediaDisplayBox(entity) ??
      getFileRecordMediaDisplayBoxFromMetadata(entity))
    : null;
  // Preserve square locking for all square cards (image/video/file).
  // Fitted media boxes (e.g. image/* attachments) also keep their computed
  // aspect. HTML file records are excluded — they resize freely.
  const keepResizeAspect = isSquareResizable || fittedMediaBox != null;
  const resizerMinW = ENTITY_RESIZER_MIN;
  const resizerMinH = ENTITY_RESIZER_MIN;
  const isResizable = isSquareResizable || isHtmlFileRecord;

  // React Flow passes width=0 for nodes that haven't been measured yet.
  // Use || (not ??) so that 0 also falls back to the default.
  const nodeWidth = width || fittedMediaBox?.width || DEFAULT_WIDTH;
  // Explicit height: fitted media and file-ish nodes need a stable host box.
  // Agent-placed file_records can arrive with only id/title and no Loro
  // dimensions, so non-media records fall back to the default file tile
  // height instead of letting the embedded thumb collapse.
  const isFileRecord =
    !!entity && "base" in entity && entity.base === "file_record";
  const isLegacyFileBase =
    !!entity && "base" in entity && entity.base === "file";
  const nodeHeight =
    height && height > 0
      ? height
      : fittedMediaBox != null
        ? fittedMediaBox.height
        : isHtmlFileRecord
          ? // Blocks render 1:1 with no `aspect-square` fallback, so a node
            // that arrives without a stored height would collapse to nothing.
            // The legacy thumb tile keeps its aspect ratio and needs no height.
            htmlBlock
            ? DEFAULT_FILE_NODE_HEIGHT
            : undefined
          : isFileRecord || isLegacyFileBase || isGenericTile
            ? DEFAULT_FILE_NODE_HEIGHT
            : undefined;

  // Get processes for this node
  const processes = getProcessesForNode(id);

  // Navigate to flow session when clicking a process
  const handleProcessNavigate = useCallback(
    (flowSessionId: string) => {
      openCanvasFloatingSession(flowSessionId);
    },
    [openCanvasFloatingSession],
  );

  // Process actions — AI/automation only (rendered purple in toolbar)
  const entityActions = useMemo(() => {
    if (!layoutEntity) return [];

    const entity = layoutEntity;
    const actions: ContextMenuAction[] = [];

    // Action item — execute
    if ("base" in entity && entity.base === "action_item") {
      const entityData = getEntityData(entity);
      const canExecute = entityData?.status === "ready";
      if (canExecute) {
        actions.push({
          key: "execute-action-item",
          label: "Execute action item",
          icon: <Play className="h-5 w-5" />,
          disabled: isProcessing(id),
          onClick: () => {
            dispatchProcess({
              nodeId: id,
              entity,
              action: "execute_action_item",
            });
          },
        });
      }
    }

    // HTML blocks — the toolbar button is the same gesture as double-click /
    // Enter: it puts the user INSIDE the block (edit when islands + canEdit,
    // else interact) and lights up while any entry is active, however it was
    // entered. Clicking while inside leaves, like Esc or the grip's Done.
    // Standard chrome, not accent: the icon reads black/white per theme until
    // the toggle is active.
    if (htmlBlock && canEdit && !data?.loading) {
      const engaged = htmlBlock.focusMode !== null;
      actions.push({
        key: "toggle-block-interactive",
        label: engaged ? "Stop interacting" : "Make interactive",
        icon: <MousePointerClick className="h-5 w-5" />,
        active: engaged,
        accent: false,
        onClick: () => {
          if (engaged) {
            htmlBlock.exit();
            return;
          }
          // A block that never advertised interactivity gets the flag
          // persisted here — the button must do what its label says, and
          // entry routes nowhere without the flag (or islands). requestEnter
          // then waits out the one-render lag before the flag reaches the
          // hook through node data.
          if (!htmlBlock.interactiveFlag) {
            canvas.updateNodeData(id, {
              presentation: { ...htmlBlock.presentation, interactive: true },
            });
          }
          htmlBlock.requestEnter();
        },
      });
    }

    // Display-mode conversion for HTML file records — the user-side repair
    // for a wrong block-vs-document choice at create time (mirrors
    // CanvasEdit's per-node `display` conversion). Currently hidden — see
    // DISPLAY_MODE_CONVERSION_TOOLBAR.
    if (
      DISPLAY_MODE_CONVERSION_TOOLBAR &&
      isHtmlFileRecord &&
      canEdit &&
      !data?.loading
    ) {
      if (htmlBlock) {
        actions.push({
          key: "show-as-document",
          label: "Show as document",
          icon: <FileText className="h-5 w-5" />,
          accent: false,
          onClick: () => {
            // Exit first so the focus latch's falling edge flushes pending
            // island saves; the marker removal then unmounts the block
            // machinery, whose unmount path cannot flush.
            htmlBlock.exit();
            setTimeout(() => canvas.removeNodeDataKey(id, "presentation"), 0);
          },
        });
      } else {
        actions.push({
          key: "show-as-block",
          label: "Show as block",
          icon: <Frame className="h-5 w-5" />,
          accent: false,
          onClick: () => {
            canvas.updateNodeData(id, { presentation: {} });
          },
        });
      }
    }

    return actions;
  }, [
    layoutEntity,
    id,
    dispatchProcess,
    isProcessing,
    htmlBlock,
    isHtmlFileRecord,
    canEdit,
    canvas,
    data?.loading,
  ]);

  const handleDelete = () => {
    revokeLocalPreview(id);
    canvas.removeNode(id);
  };

  const handleChatSubmit = async (message: string) => {
    if (!data?.entity) return;

    const flowSessionId = await dispatchProcess({
      nodeId: id,
      entity: layoutEntity ?? data.entity,
      action: "chat",
      customPrompt: message,
    });

    setShowChatInput(false);

    if (flowSessionId && canvasId) {
      openCanvasFloatingSession(flowSessionId);
    }
  };

  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  // Single-select: hover, chat input, or selection. Hidden during drag and resize.
  const showToolbar =
    !dragging &&
    !isResizing &&
    !isMultiSelectToolbarOpen &&
    (isToolbarHovered ||
      showChatInput ||
      (selected && selectedNodeCount === 1));

  const handleEntityClick = async () => {
    if (!layoutEntity) return;

    const entity = layoutEntity;
    const entityData = getEntityData(entity);
    if (!entityData?.id) return;

    const targetPane = "rightPane";

    if ("type" in entity && (entity as any).type === "skill") {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.SKILLS.SHOW, {
          id: entityData.id.toString(),
        }),
      });
      return;
    }

    if ("base" in entity && entity.base === "file_record") {
      // Blocks thread their canvas footprint + display flags along so the
      // pane can preview at the authored size (the pane is addressed by id
      // and cannot recover node data). useOpenFileRecord handles the
      // two-hop navigation pattern for standalone sessions (irrelevant on
      // canvas but safe).
      openFileRecord(
        String(entityData.id),
        htmlBlock
          ? {
              blockContext: {
                width: nodeWidth,
                height: nodeHeight ?? DEFAULT_FILE_NODE_HEIGHT,
                presentation: htmlBlock.presentation,
              },
            }
          : undefined,
      );
      return;
    }

    const isNotionDocument =
      ("type" in entity && (entity as any).type === "notion_mcp_document") ||
      ("base" in entity &&
        entity.base === "document" &&
        entity.platform === "notion");

    if (isNotionDocument) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, {
          pageId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      (entity.base === "email" || entity.base === "email_thread") &&
      entity.platform === "gmail"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, {
          emailId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      (entity.base === "issue" ||
        ("type" in entity && entity.type === "github_project_item"))
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.ISSUE_DETAIL, {
          issueId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "pull_request"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PR_DETAIL, {
          prId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "repo"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.REPO_DETAIL, {
          repoId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "project"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PROJECT_DETAIL, {
          projectId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "google-drive" &&
      entity.base === "file"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, {
          fileId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    // Generic entities carry their real platform in data.platform_name — route
    // to that platform's dedicated pane. Unmapped platforms fall through to the
    // generic entity detail pane below.
    if ("base" in entity && entity.base === "generic") {
      const target = resolveGenericEntityPaneTarget(entity);
      if (target) {
        appNavigate({
          pane: targetPane,
          to: target.to,
          options: { state: target.state },
        });
        return;
      }
    }

    appNavigate({
      pane: targetPane,
      to: route(ROUTES.ENTITIES.DETAIL, {
        platform: entity.platform || "cohesive",
        entityId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
  };

  const {
    handlers: entityNodeHandlers,
    hasWebLink,
    hasDetailView,
  } = useCanvasEntityNavigationHandlers(
    layoutEntity,
    canEdit,
    handleDelete,
    () => void handleEntityClick(),
  );

  // Canvas entities carry an id-only stub; prefer the cached API record so
  // toolbar file actions (share/publish/download) have `path` etc., matching
  // how the node context menu resolves it.
  const { data: canvasFileRecords = [] } = useCanvasFileRecords(canvasId);

  const fileRecordForExport = useMemo<FileRecord | null>(() => {
    if (
      !layoutEntity ||
      !("base" in layoutEntity) ||
      layoutEntity.base !== "file_record"
    ) {
      return null;
    }
    const stub = getEntityData(layoutEntity) as FileRecord;
    return resolveFileRecordFromList(stub, canvasFileRecords) ?? stub;
  }, [layoutEntity, canvasFileRecords]);

  const entityNodeKind = useMemo(
    () =>
      layoutEntity && "base" in layoutEntity
        ? buildCanvasNodeKind(
            "entity",
            layoutEntity.base,
            fileRecordForExport?.kind === "link",
          )
        : null,
    [layoutEntity, fileRecordForExport],
  );

  // Shared canvas file-record handler deps (same set the node context menu
  // uses) so the toolbar's More menu gets the 1:1 lifecycle actions: Delete =
  // remove node + trash record; Hide on canvas = non-destructive node.hidden.
  const canvasFileRecordHandlerDeps = useCanvasFileRecordMenuHandlerDeps(
    canvasId,
    fileRecordForExport,
  );

  const { actions: entityUtilityRegistryActions } =
    useCanvasEntityObjectActions({
      kind: entityNodeKind,
      surface: "floating-toolbar",
      enabled: showToolbar && !!entityNodeKind,
      nodeHandlers: entityNodeHandlers,
      fileRecord: fileRecordForExport,
      fileRecordHandlerDeps: canvasFileRecordHandlerDeps ?? {
        onOpen: (record) => openFileRecord(record.id),
      },
      state: {
        canEdit,
        hasWebLink,
        hasDetailView,
      },
    });

  const utilityActions = useMemo(() => {
    // Temporary simplification: file entity node toolbars show only these
    // utilities (chat + delete render separately; everything else stays
    // available via the three-dot "More" menu). Order matches this list.
    const registryActions = isFileRecordEntity
      ? FILE_ENTITY_TOOLBAR_UTILITY_ORDER.flatMap((id) =>
          entityUtilityRegistryActions.filter((a) => a.id === id),
        )
      : entityUtilityRegistryActions;

    return resolvedActionsToContextMenuActions(
      registryActions,
      ["toolbar-utility"],
      {
        kind: entityNodeKind ?? { type: "canvas-node", nodeType: "entity" },
      },
    );
  }, [entityNodeKind, entityUtilityRegistryActions, isFileRecordEntity]);

  const openNodeContextMenu = useOpenNodeContextMenu();
  const handleMoreClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      openNodeContextMenu(id, e.currentTarget);
    },
    [id, openNodeContextMenu],
  );

  if (!data) {
    return (
      <div className="bg-card border-2 rounded-lg p-4 w-[320px] flex items-center justify-center h-24">
        <div className="text-sm text-muted-foreground">Invalid Entity Node</div>
      </div>
    );
  }

  // ── Skeleton ────────────────────────────────────────────────────────────────
  // Agent placement and collaborators see the shimmer until server media
  // lands. The dropping user's image/video uploads use client-only blob URLs
  // (CanvasLocalFilePreviewContext) so the real card renders immediately.
  const fileRecordData = isFileRecordEntity
    ? (layoutEntity?.data as
        | {
            status?: string;
            url?: string;
            content_type?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : null;

  const showSkeleton = shouldShowFileRecordSkeleton({
    loading: data.loading,
    isFileRecordEntity,
    fileRecordData: isFileRecordEntity
      ? (effectiveEntity?.data as typeof fileRecordData)
      : null,
    localPreview,
    videoHandoffReady,
  });

  // Connect handles render only on the real card (below) — there's nothing
  // useful to wire an edge to on a loading skeleton or an errored node. Since
  // those handles therefore *appear* only when the node swaps from skeleton /
  // error into the card, React Flow needs to re-measure the node so edges (and
  // the live connection-line preview) attach to the freshly-mounted handles;
  // its cached handle bounds from the prior branch are stale/empty otherwise.
  // A node that mounts directly as the card already has its handles measured
  // normally, so we nudge RF only on the transition *into* the card, never on
  // initial mount. This also keeps the screenshot/capture path (where nodes
  // mount straight as cards) free of extra re-measure churn.
  const updateNodeInternals = useUpdateNodeInternals();
  const isCard = !data.error && !showSkeleton;
  const wasCardRef = useRef(isCard);
  useEffect(() => {
    if (isCard && !wasCardRef.current) {
      updateNodeInternals(id);
    }
    wasCardRef.current = isCard;
  }, [id, isCard, updateNodeInternals]);

  // ── Error state ────────────────────────────────────────────────────────────
  // URL ingestion failed: keep the node so the user can see the error message
  // and open / copy the original URL before deciding whether to delete.
  if (data.error) {
    const errW = width || DEFAULT_WIDTH;
    const errH = height || DEFAULT_FILE_NODE_HEIGHT;
    return (
      <div className="relative" style={{ width: errW, height: errH }}>
        {/* No connect handles on the error card — an errored node isn't a
            meaningful edge endpoint. */}
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={errW}
            topOffset={64}
            onPointerDown={() => setIsToolbarHovered(true)}
          >
            <div
              onMouseEnter={() => setIsToolbarHovered(true)}
              onMouseLeave={() => setIsToolbarHovered(false)}
            >
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={[]}
                utilityActions={
                  data.error.sourceUrl
                    ? (() => {
                        const definition =
                          getObjectActionDefinition("open-in-new-tab");
                        const labelCtx = {
                          kind: {
                            type: "canvas-node" as const,
                            nodeType: "entity" as const,
                          },
                        };
                        return [
                          {
                            key: "open-in-new-tab",
                            label: getActionLabel(definition, labelCtx),
                            icon: createElement(
                              getActionIcon(definition, labelCtx),
                              {
                                className: "h-[17px] w-[17px]",
                              },
                            ),
                            onClick: () =>
                              openCanvasEntityTabUrl(data.error!.sourceUrl!),
                          },
                        ];
                      })()
                    : []
                }
                onChatClick={canEdit ? () => {} : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            </div>
          </CanvasFloatingToolbarPortal>
        )}

        <div
          className={cn(
            "w-full h-full rounded-xl overflow-hidden flex flex-col bg-neutral-900 border border-neutral-700/50 shadow-md",
            selected && "ring-2 ring-canvas-selection/60",
          )}
          onMouseEnter={() => setIsToolbarHovered(true)}
          onMouseLeave={() => setIsToolbarHovered(false)}
        >
          <div className="flex-1 p-4 flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-lc-warning" />
              <span className="text-sm font-semibold leading-tight text-lc-warning">
                {data.error.title}
              </span>
            </div>
            {data.error.message && (
              <p className="text-xs leading-relaxed text-neutral-400">
                {data.error.message}
              </p>
            )}
          </div>

          {data.error.sourceUrl && (
            <button
              className="flex items-center gap-2 border-t border-neutral-700/50 px-4 py-3 text-left text-neutral-500 transition-colors hover:text-neutral-300"
              onClick={() =>
                window.open(
                  data.error!.sourceUrl,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate text-xs">{data.error.sourceUrl}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  if (showSkeleton) {
    const skeletonW = width || DEFAULT_WIDTH;
    const skeletonH = height || undefined;
    const entityType =
      (data.entity as { type?: string } | undefined)?.type ?? "";
    const contentType = fileRecordData?.content_type ?? "";
    const skeletonKind: FileRecordSkeletonKind =
      contentType.startsWith("image/") || entityType === "googleai_image"
        ? "image"
        : contentType.startsWith("video/") || entityType === "googleai_video"
          ? "video"
          : "file_record";
    const skeletonLabel =
      (layoutEntity?.data as { title?: string } | undefined)?.title ||
      undefined;

    // The agent is still writing an HTML block: paint the partial document in
    // place of the pulse skeleton, so the block visibly writes itself and the
    // handoff to the finished card is seamless.
    if (streamPreview && skeletonKind === "file_record") {
      return (
        <HtmlBlockStreamingGhost
          preview={streamPreview}
          width={skeletonW}
          height={skeletonH}
          title={skeletonLabel}
          selected={selected}
        />
      );
    }

    return (
      <div className="relative" style={{ width: skeletonW, height: skeletonH }}>
        {/* No connect handles on the loading skeleton — they appear with the
            real card once media lands (see the updateNodeInternals nudge above). */}
        <FileRecordCardSkeleton
          width={skeletonW}
          height={skeletonH}
          kind={skeletonKind}
          label={skeletonLabel}
          // Same select/hover affordances as the real card so it's obvious the
          // skeleton is a normal node — selectable and deletable (useful for
          // manually cleaning up a placeholder whose tool run was lost).
          className={cn(
            "transition-shadow",
            !selected && "hover:ring-1 hover:ring-gold/30",
            selected && "ring-2 ring-canvas-selection/60",
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative w-[320px]",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined }}
    >
      {/* React Flow edge connection handles — one grabbable source per side
          (loose connection mode lets each act as source or target). */}
      <CanvasConnectHandles />

      {/* NodeResizer — only shown when the node is selected. Outer condition
          gates the render (same pattern as ShapeNode) so the handles never
          exist in the DOM for unselected nodes. */}
      {isResizable && canEdit && !!selected && !dragging && (
        <NodeResizer
          keepAspectRatio={keepResizeAspect}
          minWidth={resizerMinW}
          minHeight={resizerMinH}
          onResizeStart={() => {
            setIsResizing(true);
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            setIsResizing(false);
            isResizingRef.current = false;
            // Settle dimensions on the lattice. Media keeps its aspect:
            // width snaps, height follows the ratio (exception register X1).
            const w = Math.max(resizerMinW, snapToGrid(params.width, LATTICE));
            const h = keepResizeAspect
              ? Math.round(w * (params.height / params.width))
              : Math.max(resizerMinH, snapToGrid(params.height, LATTICE));
            canvas.updateNodeDimensions(id, { width: w, height: h });
          }}
        />
      )}

      {/* Floating toolbar — uses CanvasFloatingToolbarPortal so useViewport()
          is only subscribed when the toolbar is actually visible. Unselected
          nodes pay zero viewport-tracking cost. */}
      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={nodeWidth}
          topOffset={64}
          onPointerDown={() => setIsToolbarHovered(true)}
        >
          <div
            onMouseEnter={() => setIsToolbarHovered(true)}
            onMouseLeave={() => setIsToolbarHovered(false)}
          >
            {showChatInput ? (
              <BaseEntityCardQuickChat
                visible={true}
                onSubmit={handleChatSubmit}
                onClose={() => setShowChatInput(false)}
              />
            ) : (
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={entityActions}
                utilityActions={utilityActions}
                onChatClick={canEdit ? () => setShowChatInput(true) : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            )}
          </div>
        </CanvasFloatingToolbarPortal>
      )}

      {/* Entity card */}
      {data.entity ? (
        <div
          className={cn("relative", htmlBlock && "group/block")}
          data-entity-type={
            "type" in data.entity ? String(data.entity.type) : undefined
          }
          data-entity-base={
            "base" in data.entity ? String(data.entity.base) : undefined
          }
        >
          <CanvasEntityCard
            entity={
              (fileRecordCardEntity ?? layoutEntity ?? data.entity) as Entity
            }
            isReference={fileRecordForExport?.kind === "link"}
            onDoubleClick={
              // Double-click means "enter the object": edit mode when the
              // user can edit and the document has islands, else interact
              // mode, else fall through to the pane. The shield's hover pill
              // announces the outcome before the gesture.
              htmlBlock?.canEnter ? htmlBlock.enter : handleEntityClick
            }
            toolbarMode="external"
            selected={selected}
            focused={htmlBlock ? htmlBlock.focusMode != null : undefined}
            resizing={isResizing}
            presentation={htmlBlock?.presentation}
            theme={htmlBlock?.theme}
            transformHtml={htmlBlock?.transformHtml ?? presign.transformHtml}
            mountPolicy={isPublicCanvasView ? "progressive" : "eager"}
            previewImageUrl={htmlPreviewImageUrl}
            isInteractive={htmlBlock?.iframeOwnsPointer}
            hintLabel={
              htmlBlock && htmlBlock.focusMode === null && !data?.loading
                ? htmlBlock.enterHintLabel
                : null
            }
            islandHint={htmlBlock?.islandHint}
            editFocusPoint={htmlBlock?.editFocusPoint}
            onRuntimeReady={htmlBlock?.onRuntimeReady}
            onOpenDetail={htmlBlock ? handleEntityClick : undefined}
            onBlockMessage={htmlBlock?.onBlockMessage}
            islandsEditable={htmlBlock?.islandsEditable}
            contentFrozen={htmlBlock?.contentFrozen}
            className={cn(
              // w-[320px] is the base class; inline style overrides it when resized.
              "rounded-lg relative w-[320px] border",
              // Disable hover shadow transition during resize to prevent flashing.
              !isResizing && "transition-shadow",
              !selected && "hover:border-gold/30",
              // Focused-for-interaction (inside the block) reads blue; a mere
              // single-click selection keeps the rust selection ring.
              selected &&
                htmlBlock?.focusMode == null &&
                "ring-2 ring-canvas-selection/60",
              htmlBlock?.focusMode != null && "ring-2 ring-spectrum-blue",
              isHighlighted &&
                "ring-2 ring-canvas-selection/80 dark:ring-canvas-selection/80",
              data?.entity && "cursor-pointer select-none",
              // Blocks own their whole box: the node chrome (border, radius,
              // hover ring) would otherwise draw a frame around a document
              // that asked to be frameless.
              htmlBlock?.presentation.frameless &&
                "rounded-none border-0 hover:border-transparent",
            )}
            style={{
              width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined,
              // Square cards (image/video/file) size themselves via aspect-square;
              // HTML file records get an explicit height to break the lock.
              ...(nodeHeight !== undefined && { height: nodeHeight }),
            }}
            onVideoDisplayReady={
              isVideoLocalPreview ? handleVideoDisplayReady : undefined
            }
          />
          {htmlBlock?.focusMode != null && (
            // Mode-gated, not gesture-gated: unmounting this mid-drag would
            // drop the pointer capture the drag depends on.
            <HtmlBlockGripStrip
              alwaysVisible={!!selected}
              editing={htmlBlock.focusMode === "edit"}
              pendingCount={htmlBlock.saveState.pendingCount}
              isSaving={htmlBlock.saveState.isSaving}
              onDoubleClick={handleEntityClick}
              onDone={htmlBlock.exit}
            />
          )}
          {processes.length > 0 && (
            // Absolutely positioned so the badges float below the card without
            // expanding the node's measured bounding box / resize handles.
            <div className="absolute top-full left-0 right-0 flex flex-wrap justify-end gap-1 mt-1.5 px-1">
              <AnimatePresence mode="popLayout">
                {processes.map((process) => (
                  <LiveProcess
                    key={process.id}
                    process={process}
                    onNavigate={handleProcessNavigate}
                    hideWhenComplete
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card border-2 rounded-lg p-4 w-full flex items-center justify-center h-24">
          <div className="text-sm text-muted-foreground">Empty Entity Node</div>
        </div>
      )}
    </div>
  );
}
